import LegalPage, { H2, P, UL } from "@/components/LegalPage";
import { BRAND } from "@/config/brand";

export default function Privacy() {
  return (
    <LegalPage eyebrow="Legal" title="Privacy Policy"
      updated="8 February 2026" testid="privacy-page">
      <P>
        Counseltaria™ (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) provides a private
        tele-counselling platform for people of every age. Because our services may
        involve minors and always involve mental-health information, we treat every piece
        of data you share with the highest possible care. This policy explains what we
        collect, how we use it, whom we share it with and the rights you have over your data.
      </P>

      <H2>1. Who this policy covers</H2>
      <P>
        This policy applies to visitors of {BRAND.website}, clients who register to book
        counselling sessions, guardians of clients aged under 18, and counsellors who
        apply or are onboarded on the platform.
      </P>

      <H2>2. Information we collect</H2>
      <UL>
        <li><strong>Account details</strong> — name, email address, chosen username, encrypted password hash, and role (client / counsellor).</li>
        <li><strong>Session data</strong> — the counsellor you booked with, service purchased, date/time, and lifecycle events (joined/left/ended) recorded to verify the session took place. We do <em>not</em> record the video or audio of any session.</li>
        <li><strong>Payment metadata</strong> — Razorpay order ID, payment ID, amount in ₹ (INR), status. Your card number, UPI ID and bank credentials are handled entirely by Razorpay and are <em>never</em> stored on our servers.</li>
        <li><strong>Counsellor onboarding</strong> — for counsellors on the &ldquo;Pay Nothing Before&rdquo; tier, we additionally collect PAN, bank/UPI details and identity verification information as required by Razorpay Route for revenue sharing.</li>
        <li><strong>Chat and support content</strong> — messages you exchange with our Mooly assistant and any email you send us.</li>
        <li><strong>Device and log data</strong> — IP address, browser type, timestamps for security and abuse-prevention purposes.</li>
      </UL>

      <H2>3. Special protection for minors (ages 12–18)</H2>
      <P>
        Counselling for anyone under 18 requires <strong>verifiable parental or guardian
        consent</strong> before their first paid session. By registering a child on the
        platform, the parent/guardian confirms they have read this policy and agree to
        the collection and use of their child's personal information for the purpose of
        counselling. We do not knowingly collect data from children under 12. If we
        become aware that we have collected such data, we will delete it promptly.
      </P>

      <H2>4. How we use your information</H2>
      <UL>
        <li>To provide the counselling service you booked — matching you with a counsellor, running the private video room, sending reminders.</li>
        <li>To process payments in ₹ via Razorpay and to disburse counsellor earnings via Razorpay Route.</li>
        <li>To communicate essential service messages: booking confirmations, counsellor-ready notifications, password resets, receipts.</li>
        <li>To improve the platform (e.g., anonymous, aggregated usage statistics).</li>
        <li>To comply with legal obligations, resolve disputes and enforce our Terms.</li>
      </UL>

      <H2>5. Session confidentiality</H2>
      <P>
        Video sessions are peer-to-peer encrypted (via Jitsi) between you and your
        counsellor. Counseltaria™ staff cannot join or listen in. Counsellors follow
        the confidentiality standards of the Indian Association of Clinical
        Psychologists and applicable professional ethics. Confidentiality may only be
        broken in the narrow circumstances required by law (e.g. imminent risk to the
        client's life or that of a third party, court order, or suspected abuse of a
        minor).
      </P>

      <H2>6. Third-party sub-processors</H2>
      <UL>
        <li><strong>Razorpay</strong> — payment gateway and Route disbursements. See razorpay.com/privacy.</li>
        <li><strong>8x8 JaaS (8x8.vc)</strong> — end-to-end encrypted video call infrastructure. We do not record calls; only short-lived, tenant-signed access tokens are used to join a private room.</li>
        <li><strong>Resend</strong> — transactional email delivery (confirmations, reminders, receipts).</li>
        <li><strong>MongoDB Atlas / hosting infrastructure</strong> — encrypted database and application hosting.</li>
      </UL>

      <H2>7. Data retention</H2>
      <UL>
        <li>Account records: retained while your account is active. On deletion, personally identifiable data is removed within 30 days.</li>
        <li>Booking and payment records: retained for a minimum of 7 years to comply with Indian tax and financial regulations, even after account deletion.</li>
        <li>Support emails: retained up to 2 years.</li>
        <li>Session lifecycle logs (joined/left/ended timestamps): retained up to 1 year for dispute resolution.</li>
      </UL>

      <H2>8. Your rights</H2>
      <UL>
        <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
        <li><strong>Correction</strong> — ask us to fix inaccurate information.</li>
        <li><strong>Deletion</strong> — delete your account from your dashboard (Danger Zone) at any time. Certain financial records will be retained per Section 7.</li>
        <li><strong>Withdrawal of consent</strong> — you may stop receiving non-essential communication at any time.</li>
        <li><strong>Complaint</strong> — you may complain to the Data Protection Board of India under the DPDP Act, 2023.</li>
      </UL>

      <H2>9. Security</H2>
      <P>
        We use HTTPS everywhere, hashed passwords, httpOnly session cookies, encrypted
        database storage and access-controlled admin operations. No system is 100%
        secure, however, and we cannot guarantee absolute security. Please choose a
        strong, unique password and keep your account credentials confidential.
      </P>

      <H2>10. Changes to this policy</H2>
      <P>
        We may update this policy from time to time. Material changes will be
        communicated via email or an in-product banner at least 7 days before they
        take effect.
      </P>

      <H2>11. Contact the Data Officer</H2>
      <P>
        Write to <a href={`mailto:${BRAND.supportEmail}`} style={{ color: "var(--brand)" }}>{BRAND.supportEmail}</a> with the subject line
        &ldquo;Privacy&rdquo; and we will respond within 7 business days.
      </P>
    </LegalPage>
  );
}
